/*
 * This file is part of glowingblue/password-strength.
 *
 * Copyright (c) 2021 Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Component from 'flarum/common/Component';
import Stream from 'flarum/common/utils/Stream';
import extractText from 'flarum/common/utils/extractText';
import EyeButton from './EyeButton';
import StrengthIndicator from './StrengthIndicator';
import { slug } from '../../common';

// Make translation calls shorter
const t = app.translator.trans.bind(app.translator);
const prfx = `${slug}.forum.strengthLabels`;
// Make settings accessible easier
const settings = key => app.forum.attribute(`${slug}.${key}`);

// The estimator bundles a large dictionary, so it is code-split into its own
// chunk and only fetched the first time a user types a password. The import
// promise is cached module-wide so the chunk is downloaded and the factory
// built at most once across all field instances.
let estimatorPromise = null;
function loadEstimator() {
  return estimatorPromise ??= import(/* webpackChunkName: 'forum/estimator', webpackMode: 'lazy-once' */ '../estimator').then(_ref => {
    let {
      default: zxcvbn
    } = _ref;
    return zxcvbn;
  });
}
export default class SignUpPasswordField extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.strengthLabel = new Stream('');
    this.passwordScore = new Stream(undefined);
    this.strengthColor = new Stream('');
  }
  view() {
    const {
      parent_this,
      showingPassword,
      hasConfirmFiled,
      isConfirmFiled
    } = this.attrs;
    const fieldLabel = isConfirmFiled ? extractText(t('nearata-signup-confirm-password.forum.field_placeholder')) : extractText(t('core.forum.sign_up.password_placeholder'));
    return (
      // This replaces the `password` field rendered by core's `SignUpModal`
      // (flarum/forum/components/SignUpModal#fields), and nearata's
      // `nearataConfirmPassword` field. Keep the label/input attributes in
      // sync with core; the additions are the `togglable` class, the
      // visibility-aware `type`, strength-based styling, the EyeButton and
      // the StrengthIndicator.
      m("div", {
        className: "Form-group PasswordField"
      }, m("label", {
        className: "label"
      }, fieldLabel), m("input", {
        className: `FormControl ${settings('enablePasswordToggle') ? 'togglable' : ''}`,
        name: isConfirmFiled ? 'confirmPassword' : 'password',
        type: showingPassword() ? 'text' : 'password',
        autocomplete: "new-password",
        placeholder: fieldLabel,
        "aria-label": fieldLabel,
        value: isConfirmFiled ? parent_this.confirmPassword() : parent_this.password(),
        disabled: parent_this.loading,
        oninput: this.inputHandler.bind(this),
        style: {
          color: settings('enableInputColor') && !showingPassword() && (!hasConfirmFiled || isConfirmFiled) ? this.strengthColor() : undefined,
          borderColor: settings('enableInputBorderColor') && (!hasConfirmFiled || isConfirmFiled) ? this.strengthColor() : undefined
        }
      }), settings('enablePasswordToggle') ? m(EyeButton, {
        showing: showingPassword
      }) : null, !hasConfirmFiled || isConfirmFiled ? m(StrengthIndicator, {
        score: this.passwordScore(),
        label: this.strengthLabel(),
        color: this.strengthColor()
      }) : null)
    );
  }
  inputHandler(e) {
    const {
      parent_this,
      isConfirmFiled
    } = this.attrs;
    const password = e.target.value;
    if (isConfirmFiled) {
      parent_this.confirmPassword(password);
    } else {
      parent_this.password(password);
    }
    if (!password) {
      this.passwordScore(undefined);
      this.strengthLabel('');
      this.strengthColor(undefined);
      return;
    }

    // Scoring requires the code-split estimator chunk. Load it on demand (the
    // promise is cached), then update the indicator and trigger a redraw. We
    // guard against an out-of-order resolution by checking the current value.
    loadEstimator().then(zxcvbn => {
      const current = isConfirmFiled ? parent_this.confirmPassword() : parent_this.password();
      if (current !== password) {
        return;
      }
      this.applyScore(zxcvbn.check(password).score);
      m.redraw();
    });
  }
  applyScore(score) {
    this.passwordScore(score);

    // Define strength color & label
    switch (score) {
      case 0:
      case 1:
        this.strengthLabel(t(`${prfx}.weak`));
        this.strengthColor(settings('weakColor'));
        break;
      case 2:
      case 3:
        this.strengthLabel(t(`${prfx}.medium`));
        this.strengthColor(settings('mediumColor'));
        break;
      case 4:
        this.strengthLabel(t(`${prfx}.strong`));
        this.strengthColor(settings('strongColor'));
        break;
    }
  }
}
flarum.reg.add('glowingblue-password-strength', 'forum/components/SignUpPasswordField', SignUpPasswordField);flarum.reg.addChunkModule('153', '340', 'glowingblue-password-strength', 'forum/estimator');